import React, { useState, useEffect, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import './Navbar.css';
import NavIcon from './NavIcons';
import { usePermissions } from '../contexts/PermissionContext';
import { useAuth } from '../contexts/AuthContext';
import { SECTION_ROUTES, NAV_SECTIONS } from '../config/routes';
import type { RouteConfig, NavSectionKey } from '../config/routes';
import { API_BASE_URL, authFetch } from '../services/api';
import { storageGet, storageSet } from '../services/storage';
import { useDashboards } from '../contexts/DashboardsContext';
import { RouteImportRing } from './import/shared/ImportProgressRing';
import ImportTicker from './import/shared/ImportTicker';
import { useUpgradeModal } from './UpgradeModal';

/** Left-click (no modifier) opens the in-app popup; modifier/middle clicks
 *  fall through to the /upgrade* route so "open in new tab" still works. */
const isPlainClick = (e: React.MouseEvent): boolean =>
  e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;

interface SelectedFrameworkNav {
  id: number;
  framework_name: string;
  framework_code: string | null;
}

interface DashboardNav {
  id: number;
  name: string;
}

interface NavbarProps {
  onNavClick?: () => void;
}

// Features hidden outright (no padlock upsell) below Enterprise. Sales is the
// only one: for Freemium it sits inside the already-fully-locked Emissions
// header, and for Pro it is hidden per spec. (Metrics & scoring is NOT here —
// it stays visible as a locked upsell row in Settings for Freemium & Pro,
// gated by the Enterprise-only ``measurement`` entitlement.)
const ENTERPRISE_ONLY_FEATURES = new Set(['sales']);
// Upstream/Scope 3 activity pages hidden specifically for Pro (Freemium already
// sees the whole Emissions section as one locked header).
const PRO_HIDDEN_FEATURES = new Set(['shipments', 'travel', 'investments']);

const Navbar: React.FC<NavbarProps> = ({ onNavClick }) => {
  const { hasFeature, userPermissions, isLoading } = usePermissions();
  const { isAuthenticated, selectedOrganisationId } = useAuth();
  const { openFeature, openSection } = useUpgradeModal();
  const location = useLocation();
  const [frameworksExpanded, setFrameworksExpanded] = useState(false);
  const [selectedFrameworks, setSelectedFrameworks] = useState<SelectedFrameworkNav[]>([]);
  // Reporting v2 dashboards submenu (the live reporting surface).
  const [reportingV2Expanded, setReportingV2Expanded] = useState(false);
  const { dashboards: sharedDashboards } = useDashboards();

  // ── Plan tier, read from the org's raw entitlements (role-independent) ──
  // Mirrors the backend ``tier_for_entitlements``: Enterprise carries
  // ``all_frameworks``; Pro carries ``reporting`` without the Enterprise set.
  // Tiering is read from the org, not the viewer's role, so a system admin
  // switching into a Freemium org sees that org's nav (locked Emissions,
  // relocated Purchases, …) rather than the full support nav — they keep page
  // access by URL, since entitlement/role gating is separate from the nav.
  const entitledFeatures = userPermissions.organisation_entitled_features;
  const isEnterpriseTier = entitledFeatures.includes('all_frameworks');
  const isProTier = !isEnterpriseTier && entitledFeatures.includes('reporting');
  const applyTiering = isAuthenticated;

  // Purchases lives under "Your network" for Freemium & Pro, returning to the
  // Emissions section only at Enterprise (which has the full Scope 3 surface).
  const effectiveSection = (route: RouteConfig): NavSectionKey =>
    applyTiering && !isEnterpriseTier && route.featureKey === 'purchases'
      ? 'value_chain'
      : route.section;

  // Collapsible sections — persisted per user. The section you're currently in
  // is always shown open regardless of the stored state.
  const [collapsedSections, setCollapsedSections] = useState<Set<NavSectionKey>>(() => {
    try {
      const raw = storageGet('navbar_collapsed_sections');
      return new Set(raw ? (JSON.parse(raw) as NavSectionKey[]) : []);
    } catch {
      return new Set();
    }
  });
  const toggleSection = (key: NavSectionKey) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      try {
        storageSet('navbar_collapsed_sections', JSON.stringify(Array.from(next)));
      } catch {
        // Non-fatal — collapse state is a convenience.
      }
      return next;
    });
  };
  const currentSection = useMemo<NavSectionKey | undefined>(() => {
    const r = SECTION_ROUTES.find(
      (rt) => location.pathname === rt.path || location.pathname.startsWith(rt.path + '/'),
    );
    return r ? effectiveSection(r) : undefined;
    // effectiveSection only reads tier flags, captured below; recompute when the
    // path or tier changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, applyTiering, isEnterpriseTier]);

  // Fetch selected frameworks for the Frameworks submenu
  useEffect(() => {
    if (!isAuthenticated || !selectedOrganisationId) {
      setSelectedFrameworks([]);
      return;
    }
    const fetchSelected = async () => {
      try {
        const res = await authFetch(
          `${API_BASE_URL}/api/frameworks/selected/?organisation_id=${selectedOrganisationId}`
        );
        if (res.ok) {
          const data = await res.json();
          setSelectedFrameworks(
            (data.selected_frameworks || []).map((sf: any) => ({
              id: sf.id,
              framework_name: sf.framework_name,
              framework_code: sf.framework_code ?? null,
            }))
          );
        }
      } catch {
        // Silent — navbar still works without the submenu
      }
    };
    fetchSelected();
  }, [isAuthenticated, selectedOrganisationId]);

  // (Reporting v1 dashboards prefetch + ``dashboardsChanged`` listener removed —
  // v1 is retired. The v2 dashboards submenu below is the only one now.)

  // Fetch the main (v2) dashboards for the "Dashboards" submenu — now the
  // default reporting surface, so it fires for every authenticated user
  // with an org (not just sys admins).
  //
  // The list comes from DashboardsContext, which owns the fetch, the org
  // switch and the `dashboardsV2Changed` invalidation for every consumer.
  // The submenu only needs id and name, so derive that at render time.
  const reportingV2Dashboards = useMemo<DashboardNav[]>(
    () => sharedDashboards.map((d) => ({ id: d.id, name: d.name })),
    [sharedDashboards],
  );

  // ── Open-alert ticker for the News & alerts navbar item ──────────
  const [unackAlertCount, setUnackAlertCount] = useState(0);

  useEffect(() => {
    if (!isAuthenticated || !selectedOrganisationId) {
      setUnackAlertCount(0);
      return;
    }
    const fetchCount = async () => {
      try {
        const res = await authFetch(
          `${API_BASE_URL}/api/frameworks/metric-alerts/?organisation_id=${selectedOrganisationId}`
        );
        if (res.ok) {
          const data = await res.json();
          setUnackAlertCount((data.open_triggers || []).length);
        }
      } catch {
        // Silent — badge just shows 0
      }
    };
    fetchCount();
  }, [isAuthenticated, selectedOrganisationId]);

  // News & alerts page emits this event whenever the user acknowledges a
  // trigger or the evaluator turns up a new one.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ count?: number }>).detail;
      if (detail && typeof detail.count === 'number') {
        setUnackAlertCount(Math.max(0, detail.count));
      }
    };
    window.addEventListener('alertsCountChanged', handler);
    return () => window.removeEventListener('alertsCountChanged', handler);
  }, []);

  // Track whether the user is on the parent page. We open the submenu when
  // they arrive there, but the user can still collapse it manually via the
  // chevron — the show-condition is just `expanded`, not `expanded || onPage`.
  const isOnFrameworkPage = location.pathname.startsWith('/frameworks');
  // Registry-driven reporting owns ``/reporting`` (v1 retired).
  const isOnReportingPage = location.pathname.startsWith('/reporting');

  useEffect(() => {
    if (isOnFrameworkPage) setFrameworksExpanded(true);
  }, [isOnFrameworkPage]);

  useEffect(() => {
    if (isOnReportingPage) setReportingV2Expanded(true);
  }, [isOnReportingPage]);

  // Group routes by section, classifying each as 'normal' (org entitled +
  // user permitted), 'locked' (org NOT entitled — show with padlock as
  // upsell), or 'hidden' (org entitled but user role lacks the feature).
  type ItemState = 'normal' | 'locked';
  type ResolvedItem = RouteConfig & { state: ItemState };

  const visibleBySection = useMemo(() => {
    const excludedCategories = new Set(
      userPermissions.inventory_excluded_categories ?? []
    );
    /** A route is hidden by GHG inventory status if every category it
     *  covers has been marked Not Relevant or Excluded. Routes without
     *  inventoryCategories are unaffected. Unlike org-entitlement gating,
     *  this DOES apply to system admins — exclusion is content state
     *  (the org said the category doesn't apply), so even support staff
     *  should see the same nav as the customer to avoid confusion. */
    const isHiddenByInventory = (route: RouteConfig): boolean => {
      if (!route.inventoryCategories || route.inventoryCategories.length === 0) {
        return false;
      }
      return route.inventoryCategories.every((code) =>
        excludedCategories.has(code)
      );
    };

    const classify = (route: RouteConfig): ItemState | 'hidden' => {
      // Logged-out: only the public ESG self-assessment is reachable.
      if (!isAuthenticated) {
        return route.featureKey === 'esg_assessment' ? 'normal' : 'hidden';
      }
      if (isLoading) return 'hidden';
      // Internal-only routes (e.g. ``Reporting v2`` during staged
      // rollout) are visible to sys admins only — falls through the
      // sys-admin branch below for them, hidden for everyone else.
      if (route.internalOnly && !userPermissions.is_system_admin) {
        return 'hidden';
      }
      // Inventory exclusion applies to EVERYONE, including system admins.
      // It's content state ("the org said this category doesn't apply"),
      // not access state, so support staff should see the same nav as
      // the customer they're helping. Checked before the sys-admin
      // bypass so admins still see hidden pages disappear.
      if (isHiddenByInventory(route)) return 'hidden';
      // Tiering is read from the org, so a system admin sees the nav of
      // whichever org they're in (they keep page access by URL). These pages
      // are hidden outright rather than shown as a locked upsell row:
      //  • Sales + Metrics & scoring are Enterprise-only everywhere.
      //  • Shipments / Travel / Investments are hidden for Pro (Freemium sees
      //    the whole Emissions section as one locked header anyway).
      if (!isEnterpriseTier && ENTERPRISE_ONLY_FEATURES.has(route.featureKey)) {
        return 'hidden';
      }
      if (isProTier && PRO_HIDDEN_FEATURES.has(route.featureKey)) {
        return 'hidden';
      }
      // Locked sections appear to everyone in a freemium org as upsell, sys
      // admins included — read from the org's RAW entitlements (not the
      // admin-bypassing isFeatureEntitled) so the lock reflects the org, not
      // the viewer. The role check below carries ALL_FEATURES for a sys admin,
      // so it only ever gates real users.
      if (!entitledFeatures.includes(route.featureKey)) return 'locked';
      if (!hasFeature(route.featureKey)) return 'hidden';
      return 'normal';
    };
    const grouped = new Map<NavSectionKey, ResolvedItem[]>();
    for (const route of SECTION_ROUTES) {
      const state = classify(route);
      if (state === 'hidden') continue;
      const section = effectiveSection(route);
      const list = grouped.get(section) ?? [];
      list.push({ ...route, state, section });
      grouped.set(section, list);
    }
    // When Purchases has been relocated into "Your network" (Freemium/Pro),
    // place it directly after Products rather than at the tail of the section.
    if (applyTiering && !isEnterpriseTier) {
      const vc = grouped.get('value_chain');
      if (vc) {
        const pIdx = vc.findIndex((i) => i.featureKey === 'purchases');
        const prodIdx = vc.findIndex((i) => i.featureKey === 'products');
        if (pIdx > -1 && prodIdx > -1 && pIdx !== prodIdx + 1) {
          const [purchasesItem] = vc.splice(pIdx, 1);
          const insertAt = vc.findIndex((i) => i.featureKey === 'products') + 1;
          vc.splice(insertAt, 0, purchasesItem);
        }
      }
    }
    return grouped;
    // effectiveSection + the tier flags are plain reads of the deps listed
    // below; eslint can't see through the helper so it's pinned manually.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAuthenticated,
    isLoading,
    userPermissions.is_system_admin,
    userPermissions.inventory_excluded_categories,
    userPermissions.organisation_entitled_features,
    hasFeature,
  ]);

  // Section-level classification:
  //   'unlocked' — every item in the section is accessible
  //   'partial'  — some items entitled, some not (per-item padlocks)
  //   'fully_locked' — no items entitled at all
  // Fully-locked sections render as a single collapsed header with a padlock,
  // grouped just above Settings; clicking opens the section upgrade page.
  type SectionState = 'unlocked' | 'partial' | 'fully_locked';
  const sectionStates = useMemo(() => {
    const states = new Map<NavSectionKey, SectionState>();
    for (const sec of NAV_SECTIONS) {
      const items = visibleBySection.get(sec.key) ?? [];
      const lockedCount = items.filter(it => it.state === 'locked').length;
      const normalCount = items.filter(it => it.state === 'normal').length;
      if (items.length === 0) {
        // No items visible at all (logged-out, all role-hidden). Treat as
        // unlocked — section won't render anyway.
        states.set(sec.key, 'unlocked');
      } else if (lockedCount === 0) {
        states.set(sec.key, 'unlocked');
      } else if (normalCount === 0) {
        states.set(sec.key, 'fully_locked');
      } else {
        states.set(sec.key, 'partial');
      }
    }
    return states;
  }, [visibleBySection]);

  // Build the render order: unlocked / partial sections first (in NAV_SECTIONS
  // order, settings deferred), then fully-locked section headers, then Settings
  // last. This pushes upsell-only sections to the bottom of the navbar so
  // accessible work surfaces stay near the top.
  const renderPlan = useMemo(() => {
    const plan: { section: typeof NAV_SECTIONS[number]; locked: boolean }[] = [];
    for (const sec of NAV_SECTIONS) {
      if (sec.key === 'settings') continue;
      if (sectionStates.get(sec.key) === 'fully_locked') continue;
      plan.push({ section: sec, locked: false });
    }
    for (const sec of NAV_SECTIONS) {
      // Settings is always part of the freemium plan — it can never appear
      // as a locked section. Guard against any classification edge case
      // that might accidentally flag it.
      if (sec.key === 'settings') continue;
      if (sectionStates.get(sec.key) === 'fully_locked') {
        plan.push({ section: sec, locked: true });
      }
    }
    const settings = NAV_SECTIONS.find(s => s.key === 'settings');
    if (settings) plan.push({ section: settings, locked: false });
    return plan;
  }, [sectionStates]);


  const renderLockedItem = (route: ResolvedItem) => (
    <NavLink
      key={route.path}
      to={`/upgrade/${route.featureKey}`}
      className="navbar-item navbar-item-locked"
      onClick={(e) => {
        if (isPlainClick(e)) {
          e.preventDefault();
          openFeature(route.featureKey);
        }
        onNavClick?.();
      }}
    >
      <NavIcon name={route.icon} size={16} />
      <span className="navbar-item-label">{route.label}</span>
      <NavIcon name="lock" size={13} />
    </NavLink>
  );

  const renderItem = (route: ResolvedItem) => {
    if (route.state === 'locked') return renderLockedItem(route);
    if (route.featureKey === 'frameworks') {
      return renderFrameworksItem(route);
    }
    if (route.featureKey === 'reporting') {
      return renderReportingV2Item(route);
    }
    const showAlertBadge =
      route.featureKey === 'news_alerts' && unackAlertCount > 0;
    return (
      <NavLink
        key={route.path}
        to={route.path}
        end={route.path === '/settings'}
        className={({ isActive }) => `navbar-item${isActive ? ' active' : ''}`}
        onClick={onNavClick}
      >
        <NavIcon name={route.icon} size={16} />
        <span className="navbar-item-label">{route.label}</span>
        {/* Progress for anything importing into this page. Reads from the
            server, so it survives navigating away and reloading. */}
        <RouteImportRing path={route.path} />
        {showAlertBadge && (
          <span
            className="navbar-item-ticker"
            aria-label={`${unackAlertCount} unacknowledged alert${unackAlertCount === 1 ? '' : 's'}`}
          >
            {unackAlertCount}
          </span>
        )}
      </NavLink>
    );
  };

  const renderFrameworksItem = (route: RouteConfig) => {
    const showSubmenu = frameworksExpanded && selectedFrameworks.length > 0;
    return (
      <div key={route.path} className="navbar-item-group">
        <div className="navbar-item-trigger">
          <NavLink
            to={route.path}
            end
            className={({ isActive }) =>
              `navbar-item${isActive || isOnFrameworkPage ? ' active' : ''}`
            }
            onClick={() => {
              // Click anywhere on the parent row toggles the submenu — same
              // behaviour as the chevron button. Navigation still happens
              // via NavLink; if the user lands on /frameworks the
              // auto-expand effect re-opens the menu, so the toggle is
              // only "visible" when they're already on the page.
              if (selectedFrameworks.length > 0) {
                setFrameworksExpanded(prev => !prev);
              }
              onNavClick?.();
            }}
          >
            <NavIcon name={route.icon} size={16} />
            <span className="navbar-item-label">{route.label}</span>
          </NavLink>
          {selectedFrameworks.length > 0 && (
            <button
              type="button"
              className="navbar-submenu-toggle"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setFrameworksExpanded(!frameworksExpanded);
              }}
              aria-label="Toggle frameworks submenu"
            >
              <NavIcon name={showSubmenu ? 'chevron-down' : 'chevron-right'} size={14} />
            </button>
          )}
        </div>
        {showSubmenu && (
          <div className="navbar-submenu">
            {selectedFrameworks.map(sf => (
              <NavLink
                key={sf.id}
                to={`/frameworks/${sf.id}`}
                className={({ isActive }) =>
                  `navbar-subitem${isActive ? ' active' : ''}`
                }
                onClick={onNavClick}
              >
                {/* Show the framework name when it's short enough to fit the rail
                    (e.g. consolidated labels like "ISSB/SASB", "ESRS/CSRD");
                    otherwise fall back to the compact code so long official
                    names ("EU Deforestation Regulation") don't overflow. */}
                {sf.framework_name && sf.framework_name.length <= 15
                  ? sf.framework_name
                  : (sf.framework_code || sf.framework_name)}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderReportingV2Item = (route: RouteConfig) => {
    // The main (registry-driven) reporting item. Talks to the v2 endpoint
    // and links to /reporting/dashboard/{id} (path param, not query string).
    // Kept as a sibling of the legacy renderDashboardsItem rather than
    // parameterising it so the URL shape and active-state logic stay obvious.
    const showSubmenu =
      reportingV2Expanded && reportingV2Dashboards.length > 0;
    return (
      <div key={route.path} className="navbar-item-group">
        <div className="navbar-item-trigger">
          <NavLink
            to={route.path}
            end
            className={({ isActive }) =>
              `navbar-item${isActive || isOnReportingPage ? ' active' : ''}`
            }
            onClick={() => {
              if (reportingV2Dashboards.length > 0) {
                setReportingV2Expanded((prev) => !prev);
              }
              onNavClick?.();
            }}
          >
            <NavIcon name={route.icon} size={16} />
            <span className="navbar-item-label">{route.label}</span>
          </NavLink>
          {reportingV2Dashboards.length > 0 && (
            <button
              type="button"
              className="navbar-submenu-toggle"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setReportingV2Expanded(!reportingV2Expanded);
              }}
              aria-label="Toggle reporting v2 dashboards submenu"
            >
              <NavIcon
                name={showSubmenu ? 'chevron-down' : 'chevron-right'}
                size={14}
              />
            </button>
          )}
        </div>
        {showSubmenu && (
          <div className="navbar-submenu">
            {[...reportingV2Dashboards]
              .sort((a, b) =>
                a.name.localeCompare(b.name, undefined, {
                  sensitivity: 'base',
                }),
              )
              .map((d) => {
                // The dashboard VIEW (/reporting/dashboard/{id}) is
                // matched by NavLink's own ``isActive``. The widget
                // BUILDER lives at /reporting with the originating
                // dashboard in a ``?dashboard=`` query param, which
                // NavLink can't see — so keep that dashboard highlighted
                // by matching the param manually (#170).
                const params = new URLSearchParams(location.search);
                const onBuilderForThis =
                  isOnReportingPage &&
                  !location.pathname.startsWith('/reporting/dashboard') &&
                  params.get('dashboard') === String(d.id);
                return (
                  <NavLink
                    key={d.id}
                    to={`/reporting/dashboard/${d.id}`}
                    className={({ isActive }) =>
                      `navbar-subitem${
                        isActive || onBuilderForThis ? ' active' : ''
                      }`
                    }
                    onClick={onNavClick}
                  >
                    {d.name}
                  </NavLink>
                );
              })}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <nav className="main-navbar">
        {renderPlan.map(({ section, locked }) => {
          if (locked) {
            return (
              <div key={section.key} className="navbar-section navbar-section-locked-wrap">
                <NavLink
                  to={`/upgrade-section/${section.key}`}
                  className="navbar-locked-section-link"
                  onClick={(e) => {
                    if (isPlainClick(e)) {
                      e.preventDefault();
                      openSection(section.key);
                    }
                    onNavClick?.();
                  }}
                >
                  <span>{section.label}</span>
                  <NavIcon name="lock" size={13} />
                </NavLink>
              </div>
            );
          }
          const items = visibleBySection.get(section.key);
          if (!items || items.length === 0) return null;
          const collapsed =
            collapsedSections.has(section.key) && section.key !== currentSection;
          return (
            <div
              key={section.key}
              className={`navbar-section${collapsed ? ' collapsed' : ''}`}
            >
              <button
                type="button"
                className="navbar-section-header"
                onClick={() => toggleSection(section.key)}
                aria-expanded={!collapsed}
              >
                <span>{section.label}</span>
                <span className="navbar-section-chevron" aria-hidden="true">
                  ▾
                </span>
              </button>
              {!collapsed && items.map(renderItem)}
            </div>
          );
        })}
      </nav>
      {/* Running imports live on the server, so this survives navigation and
          reload — see ImportJobContext. */}
      <ImportTicker />
    </>
  );
};

export default Navbar;
